// CodeBlock.js - Mobile-overflow-safe version
import React, { useState, useMemo, useCallback } from 'react';
import {
    Box,
    Button,
    HStack,
    Text,
    Code,
    useClipboard,
    IconButton,
} from '@chakra-ui/react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { prism } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { CopyIcon, CheckIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

const CodeBlock = React.memo(({ node, inline, className, children, ...props }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const codeData = useMemo(() => {
        const match = /language-(\w+)/.exec(className || '');
        const language = match ? match[1] : 'text';
        const codeString = String(children).replace(/\n$/, '');
        const isLarge = codeString.length > 5000;
        return { match, language, codeString, isLarge };
    }, [className, children]);

    const { match, language, codeString, isLarge } = codeData;
    const { hasCopied, onCopy } = useClipboard(codeString);
    const shouldTruncate = isLarge && !isExpanded;

    const displayContent = useMemo(() => {
        if (shouldTruncate) {
            return codeString.substring(0, 2000) + '\n\n... (truncated)';
        }
        return codeString;
    }, [codeString, shouldTruncate]);

    const toggleExpanded = useCallback(() => setIsExpanded(prev => !prev), []);
    const copyToClipboard = useCallback(() => onCopy(), [onCopy]);

    return !inline && match ? (
        <Box
            border="1px"
            borderColor="gray.200"
            borderRadius="lg"
            overflow="hidden"
            my={4}
            bg="transparent"
            // CRITICAL: min-width 0 lets this shrink inside a flex parent
            // instead of pushing the whole chat wide. maxW 100% caps it.
            minW={0}
            maxW="100%"
            w="full"
        >
            {/* Header */}
            <HStack
                justify="space-between"
                p={3}
                bg="gray.50"
                borderBottom="1px"
                borderColor="gray.200"
            >
                <Text
                    fontSize="xs"
                    fontWeight="600"
                    color="gray.600"
                    textTransform="uppercase"
                    fontFamily="mono"
                    letterSpacing="wider"
                >
                    {language}
                </Text>
                <HStack spacing={2}>
                    {isLarge && (
                        <Button
                            size="xs"
                            variant="ghost"
                            leftIcon={isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                            onClick={toggleExpanded}
                            color="gray.600"
                            _hover={{ bg: 'gray.100', color: 'gray.800' }}
                            fontSize="xs"
                        >
                            {isExpanded ? 'Collapse' : 'Expand'}
                        </Button>
                    )}
                    <IconButton
                        size="xs"
                        variant="ghost"
                        icon={hasCopied ? <CheckIcon /> : <CopyIcon />}
                        onClick={copyToClipboard}
                        color={hasCopied ? "green.600" : "gray.600"}
                        _hover={{
                            bg: 'gray.100',
                            color: hasCopied ? "green.700" : "gray.800"
                        }}
                        aria-label="Copy code"
                    />
                </HStack>
            </HStack>

            {/* Code Body — horizontal scroll is CONTAINED here only */}
            <Box
                position="relative"
                overflowX="auto"
                minW={0}
                maxW="100%"
                // Smooth momentum scroll for the code block on iOS,
                // and prevent the scroll from chaining out to the page.
                sx={{
                    WebkitOverflowScrolling: 'touch',
                    overscrollBehaviorX: 'contain',
                }}
            >
                <SyntaxHighlighter
                    style={prism}
                    language={language}
                    PreTag="div"
                    customStyle={{
                        margin: 0,
                        padding: '16px',
                        background: 'transparent',
                        fontSize: '13px',          // slightly smaller helps mobile
                        fontFamily: "'JetBrains Mono', 'Courier New', monospace",
                        fontWeight: "500",
                        lineHeight: '1.4',
                        // Let content be as wide as it needs; the parent Box
                        // provides the scrollable viewport.
                        width: 'max-content',
                        minWidth: '100%',
                    }}
                    codeTagProps={{
                        style: {
                            fontFamily: "'JetBrains Mono', 'Courier New', monospace",
                        }
                    }}
                    {...props}
                >
                    {displayContent}
                </SyntaxHighlighter>

                {shouldTruncate && (
                    <Box p={3} bg="gray.50" borderTop="1px" borderColor="gray.200">
                        <Text fontSize="xs" color="gray.500" textAlign="center">
                            Code block truncated ({codeString.length.toLocaleString()} characters total)
                        </Text>
                    </Box>
                )}
            </Box>
        </Box>
    ) : (
        <Code variant="inline" {...props}>
            {children}
        </Code>
    );
}, (prevProps, nextProps) =>
    prevProps.className === nextProps.className &&
    prevProps.children === nextProps.children &&
    prevProps.inline === nextProps.inline
);

export default CodeBlock;